/**
 * geo.js — the visitor's country, resolved once.
 *
 * Why a script: the country cannot be resolved server-side. Liquid has no visitor IP,
 * and `localization.country` is the MARKET country, not the visitor's. A visitor from a
 * country in no active market gets the store's backup region, AT (measured 2026-09-28), so it
 * answers AT for a Swedish shopper today. The reg-no plate asks this module directly. The
 * dealer surfaces ask it only through ValerydDealer.country() (assets/dealer-context.js), which
 * puts the market first and uses the IP only for its fallback list.
 *
 * Interface: `window.ValerydGeo.resolve(endpoint)` → Promise<{country, supported?} | null>.
 *   - `country` is upper-cased ISO 3166-1 alpha-2.
 *   - `supported` is the proxy's own verdict on whether the dealer network covers that
 *     country; callers that care read it, callers that do not ignore it.
 *   - `null` means "unknown": no endpoint, network error, non-2xx, timeout, or a body
 *     without a country. Callers treat null as "leave the page exactly as rendered".
 *
 * Guarantees: at most one network request per page (an in-flight promise is shared),
 * none at all within a session once answered (sessionStorage, written only with Cookiebot
 * preferences consent: a country detected for the visitor is a preference they did not choose,
 * VAL-881), and a 6 s ceiling — a
 * proxy that accepts the connection and never answers resolves to null, so nothing
 * downstream waits for the life of the page.
 */
(() => {
  if (window.ValerydGeo) return;

  const GEO_KEY = 'valeryd:geo:v1';
  const GEO_TIMEOUT_MS = 6000;
  let pending = null;

  // Engines without AbortSignal.timeout keep the previous unbounded behaviour rather than
  // throwing while the fetch options are being built.
  function timeoutSignal(ms) {
    try {
      return AbortSignal.timeout(ms);
    } catch (e) {
      return undefined;
    }
  }

  function normalise(geo) {
    if (!geo || !geo.country) return null;
    const out = { country: String(geo.country).toUpperCase() };
    if (typeof geo.supported === 'boolean') out.supported = geo.supported;
    return out;
  }

  function cached() {
    try {
      const raw = sessionStorage.getItem(GEO_KEY);
      return raw ? normalise(JSON.parse(raw)) : null;
    } catch (e) {
      return null;
    }
  }

  function remember(geo) {
    const consent = window.Cookiebot && window.Cookiebot.consent;
    if (!consent || !consent.preferences) return;
    try {
      sessionStorage.setItem(GEO_KEY, JSON.stringify(geo));
    } catch (e) {
      /* private mode — one lookup per page instead of per session */
    }
  }

  function resolve(endpoint) {
    const hit = cached();
    if (hit) return Promise.resolve(hit);
    if (!endpoint) return Promise.resolve(null);
    if (!pending) {
      pending = fetch(endpoint, { headers: { Accept: 'application/json' }, signal: timeoutSignal(GEO_TIMEOUT_MS) })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          const geo = normalise(body);
          if (geo) remember(geo);
          return geo;
        })
        .catch(() => null);
    }
    return pending;
  }

  window.ValerydGeo = { resolve };
})();
